import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type LoadCarrierFormSchema, loadCarrierFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { LoadCarrierForm } from "@/forms/load-carrier-form";
import { client } from "@/hono";

/** Props Interface */
interface AddLoadCarrierModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
}

/**
 * Create a load carrier. It opens on a balance of nothing and stays there — the yard's stock
 * is booked on the carrier's own page, so that a balance always has a movement explaining it.
 */
export function AddLoadCarrierModal({ onClose, isOpen, onComplete }: AddLoadCarrierModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(loadCarrierFormSchema),
		defaultValues: {
			warningStockBalance: 0,
			minimumStockBalance: 0,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: LoadCarrierFormSchema) {
		const response = await client.stock["load-carriers"].$post({ json });
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.add_load_carrier")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<LoadCarrierForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
